<template>
	<div :data-amount="product.amountOrdered" :data-max-amount="product.maxAmountToOrder" class="">
		<button v-show="showMinusButton"
		        :disabled="disableMinusButton"
		        class="remove"
		        type="button"
		        @click="changeAmount(-product.incrementAmount)">

			<span class="sr-only">Verwijderen</span>
			<span aria-hidden="true">
				<IconRemove :size="24" />
			</span>
		</button>

		<span v-show="showAmount" class="amount">
			{{ product.amountOrdered }}
		</span>

		<button v-show="showPlusButton"
		        :disabled="disablePlusButton"
		        class="add"
		        type="button"
		        @click="changeAmount(product.incrementAmount)">
			<span class="sr-only">Toevoegen</span>
			<span aria-hidden="true">
				<IconAdd :size="24" />
			</span>
		</button>
	</div>
</template>

<script lang="ts" setup>
	import type {Product} from '@/types/product';
	import {computed} from 'vue';
	import IconRemove from '@/components/icons/IconRemove.vue';
	import IconAdd from '@/components/icons/IconAdd.vue';

	const emits = defineEmits(['updateAmount']);

	const props = defineProps<{
		product: Product
	}>();

	const orderTypeAmount = computed(() => props.product.orderType === 'AMOUNT');

	const showAmount = computed(() => {
		const {amountOrdered, maxAmountToOrder} = props.product;
		return amountOrdered > 0 && (maxAmountToOrder > 1 || orderTypeAmount.value);
	});

	const showMinusButton = computed(() => props.product.amountOrdered > 0);

	const showPlusButton = computed(() => {
		const {amountOrdered, maxAmountToOrder} = props.product;
		return !amountOrdered || maxAmountToOrder > 1 || orderTypeAmount.value;
	});

	const disablePlusButton = computed(() => {
		const {disabled, amountOrdered, maxAmountToOrder, unavailable} = props.product;
		return disabled || (amountOrdered >= maxAmountToOrder && !unavailable);
	});

	const disableMinusButton = computed(() => {
		const {disabled, amountOrdered, mandatory} = props.product;
		return disabled || amountOrdered < 1 || (amountOrdered === 1 && mandatory);
	});

	const changeAmount = (amount: number) => {
		const newAmount = props.product.amountOrdered + amount;
		emits('updateAmount', newAmount);
	};

</script>

<style lang="scss" scoped>
	div {
		display: flex;
		align-items: center;
		justify-content: end;
	}

	div > * {
		flex: 0 0 auto;
	}

	.amount {
		min-width: 2.5em;
		text-align: center;
		padding: 2px .25em 0;
	}

	button {
		background: none;
		border: 1px solid currentColor;
		width: 30px;
		height: 30px;
		display: grid;
		align-items: center;
		justify-items: center;
		border-radius: 50%;
		padding: 0;

		svg {
			fill: currentColor;
		}

		&:hover {
			color: var(--color-primary_darkblue);
		}

		&:active {
			color: var(--color-primary_blue);
		}

		&:not([disabled]) {
			cursor: pointer;
		}
	}

	button[disabled] {
		&,
		&:hover,
		&:focus,
		&:active {
			color: #b4b4b4;
		}
	}

	button.add:not([disabled]) {
		border-color: var(--color-primary_blue);
		background: var(--color-primary_blue);
		color: #ffffff;

		&:hover:not(:active) {
			border-color: var(--color-primary_darkblue);
			background: var(--color-primary_darkblue);
		}
	}

	button.remove:not([disabled]) {
		color: var(--color-primary_blue);

		&:hover:not(:active) {
			background: var(--color-primary_lightblue);
			color: var(--color-primary_darkblue);
		}
	}
</style>
